import React, { useState, useContext, useEffect } from "react";
import { Box, Typography, Stack, Button, Grid, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { API, graphqlOperation, Storage } from "aws-amplify";
import {
  createInvoice,
  updateUser,
  updateInvoice,
  createPart,
} from "../../graphql/mutations";
import { getInvoice, getPricelist } from "../../graphql/queries";
import { AppContext } from "../../App";
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePDF from "./InvoicePDF";
import PrintIcon from "@mui/icons-material/Print";
import InnerPage from "../InnerPage";
import {
  fetchUsers,
  fetchCustomersForProvider,
} from "../../services/userManagementService";

// Import refactored components
import InvoiceHeader from "./InvoiceHeader";
import InvoiceStats from "./InvoiceStats";
import PartsList from "./PartsList";
import ActionButtons from "./ActionButtons";
import {
  SearchModal,
  CustomPartModal,
  UnitByWeightModal,
} from "./InvoiceModals";
import { CustomerSelector, CustomerCard } from "../shared";
import UserDialog from "../shared/UserDialog";

// Custom query to get invoice with parts
const getInvoiceWithParts = /* GraphQL */ `
  query GetInvoice($id: ID!) {
    getInvoice(id: $id) {
      id
      name
      invoiceNumber
      status
      ownerID
      recipientID
      partCount
      createdAt
      updatedAt
      parts {
        items {
          id
          partNumber
          price
          grade
          status
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export default function CreateInvoice() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { userRecord } = useContext(AppContext);
  const { invoiceId } = useParams();
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState({
    wholeUnits: 0,
    halfUnits: 0,
    totalUnits: 0,
    currentTotal: 0,
    averagePerUnit: 0,
  });
  const [formData, setFormData] = useState({
    companyName: "",
    priceList: "",
    invoiceNumber: "",
  });
  const [selectedPricelist, setSelectedPricelist] = useState(null);
  const [customPartOpen, setCustomPartOpen] = useState(false);
  const [unitByWeightOpen, setUnitByWeightOpen] = useState(false);
  const [pricelistName, setPricelistName] = useState("");

  // Sort and filter state
  const [sortOption, setSortOption] = useState("newest");
  const [filterOption, setFilterOption] = useState("all");

  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const fetchInvoiceDetails = async (invoiceId) => {
    try {
      const response = await API.graphql(
        graphqlOperation(getInvoiceWithParts, { id: invoiceId }),
      );

      const invoice = response.data.getInvoice;

      // Calculate statistics
      let wholeCount = 0;
      let halfCount = 0;
      let totalValue = 0;

      if (invoice.parts && invoice.parts.items) {
        invoice.parts.items.forEach((part) => {
          if (part.grade === 1) wholeCount++;
          if (part.grade === 0.5) halfCount++;
          totalValue += parseFloat(part.price);
        });
      }

      // Calculate averages with and without aftermarkets
      const allParts = invoice.parts?.items || [];
      const partsForAverage = allParts.filter(
        (p) =>
          p?.status !== "empty" &&
          p?.status !== "aftermarket" &&
          String(p?.partNumber || "")
            .trim()
            .toLowerCase() !== "after market",
      );
      const allPartsForAverage = allParts.filter((p) => p?.status !== "empty"); // Include aftermarkets but exclude empties

      const stats = {
        wholeUnits: wholeCount,
        halfUnits: halfCount,
        totalUnits: wholeCount + halfCount,
        currentTotal: totalValue,
        averagePerUnit:
          partsForAverage.reduce(
            (sum, p) => sum + parseFloat(p.price || 0),
            0,
          ) / (partsForAverage.length || 1),
        averagePerUnitWithAftermarkets:
          allPartsForAverage.reduce(
            (sum, p) => sum + parseFloat(p.price || 0),
            0,
          ) / (allPartsForAverage.length || 1),
      };

      setInvoiceStats(stats);
      setCurrentInvoice(invoice);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
    }
  };

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceDetails(invoiceId);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (currentInvoice) {
      setFormData((prev) => ({
        ...prev,
        customerName: currentInvoice.customerName || "",
        companyName: currentInvoice.name || "",
        invoiceNumber: currentInvoice.invoiceNumber || "",
        email: currentInvoice.email || "",
        priceList: currentInvoice.priceList || "",
      }));
    }
  }, [currentInvoice]);

  const handlePricelistUpdate = (updatedUser) => {
    setSelectedPricelist(updatedUser.activePricelistID);
    // Store in formData to maintain consistency
    setFormData((prev) => ({
      ...prev,
      priceList: updatedUser.activePricelistID,
    }));
  };

  const handleSubmit = async () => {
    try {
      const invoiceInput = {
        name: formData.companyName,
        invoiceNumber: formData.invoiceNumber,
        status: "open",
        ownerID: userRecord.id,
        recipientID: selectedPricelist,
        partCount: currentInvoice?.partCount || 0,
      };

      if (invoiceId) {
        // Update existing invoice
        invoiceInput.id = invoiceId;
        const response = await API.graphql(
          graphqlOperation(updateInvoice, { input: invoiceInput }),
        );
        const updatedInvoice = response.data.updateInvoice;
        setCurrentInvoice(updatedInvoice);
      } else {
        // Create new invoice
        const response = await API.graphql(
          graphqlOperation(createInvoice, { input: invoiceInput }),
        );
        const newInvoice = response.data.createInvoice;
        setCurrentInvoice(newInvoice);

        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: userRecord.id,
              activeInvoiceID: newInvoice.id,
            },
          }),
        );
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
    }
  };

  useEffect(() => {
    const fetchPricelistName = async () => {
      if (!userRecord?.activePricelistID) return;
      try {
        const response = await API.graphql(
          graphqlOperation(getPricelist, { id: userRecord.activePricelistID }),
        );
        const pricelist = response.data.getPricelist;
        if (pricelist && pricelist.name) {
          setPricelistName(pricelist.name);
        }
      } catch (error) {
        console.error("Error fetching pricelist name:", error);
      }
    };
    fetchPricelistName();
  }, [userRecord?.activePricelistID]);

  const fetchCustomers = async () => {
    try {
      // Use fetchCustomersForProvider to get customers from the relationship field
      // This matches the approach used in the /users page
      // Pass the full userRecord object, not just the ID
      const customers = await fetchCustomersForProvider(userRecord);

      setCustomers(customers);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  useEffect(() => {
    if (userRecord?.id) {
      fetchCustomers();
    }
  }, [userRecord?.id]);

  const handleCustomerSelect = (customer) => {
    if (customer) {
      setSelectedCustomer(customer.id);
      setFormData((prev) => ({
        ...prev,
        companyName: customer.companyName || "",
        recipientID: customer.id,
      }));
    } else {
      setSelectedCustomer(null);
    }
  };

  const handleEditCustomer = (customer) => {
    // Open the user dialog to edit the customer
    setUserDialogOpen(true);
    setEditingCustomer(customer);
  };

  // Function to get sorted and filtered parts
  const getSortedAndFilteredParts = (parts) => {
    if (!parts || !Array.isArray(parts)) return [];

    // Apply filter first
    let filteredParts = [...parts];
    if (filterOption === "wholeUnits") {
      filteredParts = parts.filter((part) => part.grade === 1);
    } else if (filterOption === "halfUnits") {
      filteredParts = parts.filter((part) => part.grade === 0.5);
    }

    // Then apply sort
    switch (sortOption) {
      case "newest":
        return filteredParts.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
      case "oldest":
        return filteredParts.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        );
      case "partNumber":
        return filteredParts.sort((a, b) =>
          a.partNumber.localeCompare(b.partNumber),
        );
      case "priceHighToLow":
        return filteredParts.sort((a, b) => (b.price || 0) - (a.price || 0));
      case "priceLowToHigh":
        return filteredParts.sort((a, b) => (a.price || 0) - (b.price || 0));
      default:
        return filteredParts;
    }
  };

  // Handlers for quick-add buttons
  const handleAddEmptyPart = async () => {
    try {
      if (!currentInvoice) return;
      const input = {
        partNumber: "Empty",
        price: 0,
        grade: 0,
        status: "empty",
        addedByUser: userRecord.id,
        fromPricelist: userRecord.activePricelistID,
        invoicePartsId: currentInvoice.id,
      };
      await API.graphql(graphqlOperation(createPart, { input }));
      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: currentInvoice.id,
            partCount: (currentInvoice.partCount || 0) + 1,
          },
        }),
      );
      fetchInvoiceDetails(currentInvoice.id);
    } catch (e) {
      console.error("Error adding empty part:", e);
    }
  };

  const handleAddAftermarketPart = async () => {
    try {
      if (!currentInvoice || !userRecord?.activePricelistID) return;
      // Get pricelist to find S3 key
      const plResp = await API.graphql(
        graphqlOperation(getPricelist, { id: userRecord.activePricelistID }),
      );
      const s3Key = plResp?.data?.getPricelist?.s3Key;
      if (!s3Key) throw new Error("Pricelist S3 key not found");

      const file = await Storage.get(s3Key, { download: true });
      const text = await file.Body.text();
      const json = JSON.parse(text);
      const item = (json || []).find(
        (row) =>
          String(row["PART NUMBER"] || "")
            .trim()
            .toLowerCase() === "after market",
      );
      const basePrice = item ? (item.adjustedPrice ?? item.PRICE ?? 0) : 0;
      const price = Math.round(parseFloat(basePrice || 0));

      const input = {
        partNumber: "After Market",
        price: isNaN(price) ? 0 : price,
        grade: 1,
        status: "aftermarket",
        addedByUser: userRecord.id,
        fromPricelist: userRecord.activePricelistID,
        invoicePartsId: currentInvoice.id,
      };
      await API.graphql(graphqlOperation(createPart, { input }));
      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: {
            id: currentInvoice.id,
            partCount: (currentInvoice.partCount || 0) + 1,
          },
        }),
      );
      fetchInvoiceDetails(currentInvoice.id);
    } catch (e) {
      console.error("Error adding aftermarket part:", e);
    }
  };

  return (
    <InnerPage>
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          minHeight: "100vh",
          padding: "20px",
          marginTop: "20px",
        }}
      >
        {!currentInvoice ? (
          <>
            {/* Customer Selection */}
            <CustomerSelector
              onCustomerSelect={handleCustomerSelect}
              theme={theme}
            />

            {/* Customer Info Display (after selection) */}
            {selectedCustomer && (
              <Box sx={{ mt: 4 }}>
                <CustomerCard
                  customer={
                    customers.find((c) => c.id === selectedCustomer) ||
                    customers.find((c) => c.id === formData.recipientID)
                  }
                  selected={true}
                  onSelect={handleCustomerSelect}
                  onEdit={handleEditCustomer}
                  theme={theme}
                />
              </Box>
            )}

            {/* Invoice/PO Number Input */}
            {selectedCustomer && (
              <Box
                sx={{
                  mt: 4,
                  mb: 2,
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: "18px",
                    marginBottom: "10px",
                  }}
                >
                  Invoice / PO Number
                </Typography>
                <TextField
                  fullWidth
                  value={formData.invoiceNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      invoiceNumber: e.target.value,
                    }))
                  }
                  placeholder="i.e., HR 24001"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "30px",
                      backgroundColor: theme.palette.background.paper,
                      "& fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: theme.palette.text.primary,
                      padding: "15px 20px",
                      fontSize: "18px",
                    },
                  }}
                />
              </Box>
            )}

            {/* Create Invoice Button */}
            {selectedCustomer && formData.invoiceNumber && (
              <Box sx={{ maxWidth: "800px", margin: "0 auto", mt: 2, mb: 4 }}>
                <Button
                  onClick={handleSubmit}
                  sx={{
                    backgroundColor: "#24e4ff",
                    color: "#000",
                    borderRadius: "30px",
                    padding: "15px",
                    fontSize: "20px",
                    fontWeight: "bold",
                    width: "100%",
                    "&:hover": {
                      backgroundColor: "#35d33d",
                    },
                  }}
                >
                  Create Invoice
                </Button>
              </Box>
            )}

            {/* User Edit Dialog */}
            <UserDialog
              user={editingCustomer}
              open={userDialogOpen}
              onClose={() => {
                setUserDialogOpen(false);
                setEditingCustomer(null);
                // Refresh customers after edit
                fetchCustomers();
              }}
              onEditUser={(updatedUser) => {
                // Update customer data if editing
                if (editingCustomer && updatedUser.id === editingCustomer.id) {
                  setEditingCustomer(updatedUser);
                }
              }}
            />
          </>
        ) : (
          <>
            {/* Invoice Header */}
            <InvoiceHeader formData={formData} />

            {/* Stats Box */}
            <InvoiceStats invoiceStats={invoiceStats} />

            {/* Parts List */}
            <PartsList
              currentInvoice={currentInvoice}
              getSortedAndFilteredParts={getSortedAndFilteredParts}
              sortOption={sortOption}
              setSortOption={setSortOption}
              filterOption={filterOption}
              setFilterOption={setFilterOption}
              fetchInvoiceDetails={fetchInvoiceDetails}
            />

            {/* Action Buttons */}
            <ActionButtons
              setSearchOpen={setSearchOpen}
              setCustomPartOpen={setCustomPartOpen}
              setUnitByWeightOpen={setUnitByWeightOpen}
              currentInvoice={currentInvoice}
              formData={formData}
              invoiceStats={invoiceStats}
              onAddEmptyPart={handleAddEmptyPart}
              onAddAftermarketPart={handleAddAftermarketPart}
            />

            {/* Modals */}
            <SearchModal
              open={searchOpen}
              onClose={() => setSearchOpen(false)}
              pricelistName={pricelistName}
              currentInvoice={currentInvoice}
              userRecord={userRecord}
              fetchInvoiceDetails={fetchInvoiceDetails}
            />

            <CustomPartModal
              open={customPartOpen}
              onClose={() => setCustomPartOpen(false)}
              currentInvoice={currentInvoice}
              userRecord={userRecord}
              fetchInvoiceDetails={fetchInvoiceDetails}
            />

            <UnitByWeightModal
              open={unitByWeightOpen}
              onClose={() => setUnitByWeightOpen(false)}
              currentInvoice={currentInvoice}
              userRecord={userRecord}
              fetchInvoiceDetails={fetchInvoiceDetails}
            />
          </>
        )}
      </Box>
    </InnerPage>
  );
}
